/**
 * Thin fetch wrapper around the gateway API.
 *
 * Two behaviours worth knowing about:
 *
 * 1. The access token lives in memory only. The refresh token is an httpOnly
 *    cookie, so a page reload recovers the session by calling /auth/refresh
 *    rather than by reading a token out of localStorage. That costs one request
 *    on boot and removes the whole class of XSS token theft.
 * 2. A 401 triggers a single refresh attempt, and concurrent 401s share it.
 *    Without that, a dashboard that fires eight queries on mount would kick off
 *    eight refreshes and invalidate each other's rotated token.
 */

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly reference?: string,
    readonly details?: string[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshInFlight: Promise<string | null> | null = null;
const listeners = new Set<(token: string | null) => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const listener of listeners) listener(token);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onTokenChange(listener: (token: string | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) return null;
        const body = (await response.json()) as { accessToken: string };
        setAccessToken(body.accessToken);
        return body.accessToken;
      } catch {
        return null;
      } finally {
        // Cleared on the next tick so callers awaiting the same promise all
        // resolve against this attempt rather than starting a second one.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, unknown>;
  /** Set for endpoints that return a file rather than JSON. */
  raw?: boolean;
  skipAuthRetry?: boolean;
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const entry of value) params.append(key, String(entry));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function toError(response: Response): Promise<ApiError> {
  let message = response.statusText || 'Request failed';
  let reference: string | undefined;
  let details: string[] | undefined;

  try {
    const body = await response.json();
    if (Array.isArray(body.message)) {
      details = body.message as string[];
      message = details[0] ?? message;
    } else if (typeof body.message === 'string') {
      message = body.message;
    }
    reference = body.reference;
  } catch {
    // A non-JSON error body (nginx 502, for instance) leaves the status text.
  }

  return new ApiError(response.status, message, reference, details);
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, raw, skipAuthRetry, headers, ...rest } = options;

  const send = async (token: string | null): Promise<Response> =>
    fetch(buildUrl(path, query), {
      ...rest,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(headers as Record<string, string> | undefined),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  let response = await send(accessToken);

  if (response.status === 401 && !skipAuthRetry) {
    const renewed = await refreshAccessToken();
    if (renewed) {
      response = await send(renewed);
    } else {
      setAccessToken(null);
    }
  }

  if (!response.ok) throw await toError(response);

  if (raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>) =>
    request<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown, query?: Record<string, unknown>) =>
    request<T>(path, { method: 'POST', body, query }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Returns the raw Response so the caller can stream or download it. */
  download: (path: string, query?: Record<string, unknown>) =>
    request<Response>(path, { method: 'GET', query, raw: true }),
  refresh: refreshAccessToken,
};
